/**
 * Company Context Provider
 *
 * Provides company information and companyId throughout the application
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { useCompanyId } from '@/hooks/useCompanyId';
import { getCompany } from '@/services/api/companyService';
import type { Company } from '@/types/company.types';

/**
 * Company context value
 */
interface CompanyContextValue {
  // Company ID from URL
  companyId: string;
  companyIdNumber: number | null;
  hasCompanyId: boolean;
  isValid: boolean;

  // Company data (loaded from API)
  company: Company | null;

  // Loading states
  isLoadingCompany: boolean;
  companyError: Error | null;

  // Actions
  refreshCompany: () => Promise<void>;
}

const CompanyContext = createContext<CompanyContextValue | undefined>(undefined);

/**
 * Company Provider Props
 */
interface CompanyProviderProps {
  children: React.ReactNode;
}

/**
 * Company Provider Component
 *
 * Wraps the application to provide company context
 */
export function CompanyProvider({ children }: CompanyProviderProps) {
  const { companyId, companyIdNumber, hasCompanyId, isValid } = useCompanyId();

  const [company, setCompany] = useState<Company | null>(null);
  const [isLoadingCompany, setIsLoadingCompany] = useState<boolean>(false);
  const [companyError, setCompanyError] = useState<Error | null>(null);

  /**
   * Load company data from API
   */
  const loadCompany = async () => {
    if (!companyIdNumber) {
      setCompany(null);
      return;
    }

    try {
      setIsLoadingCompany(true);
      setCompanyError(null);

      // Call API to get company details
      const companyData = await getCompany(companyIdNumber);
      setCompany(companyData);
    } catch (error) {
      console.error('[CompanyProvider] Failed to load company:', error);
      setCompanyError(error instanceof Error ? error : new Error('Failed to load company'));
      setCompany(null);
    } finally {
      setIsLoadingCompany(false);
    }
  };

  /**
   * Load company when companyId changes
   */
  useEffect(() => {
    loadCompany();
  }, [companyIdNumber]);

  const value: CompanyContextValue = {
    companyId: companyId || (companyId === null ? '' : companyId),
    companyIdNumber,
    hasCompanyId,
    isValid,
    company,
    isLoadingCompany,
    companyError,
    refreshCompany: loadCompany,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

/**
 * Hook to use company context
 *
 * @throws Error if used outside CompanyProvider
 */
export function useCompany(): CompanyContextValue {
  const context = useContext(CompanyContext);

  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }

  return context;
}
